import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useSignalR } from '../../services/signalR/SignalRProvider';
import { DateTimeAgoFormat, DateTimeFormat } from '../../helper/Formatters';
import FlexMessageRenderer from '../../components/chat/FlexMessageRenderer';

// ─── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ src, name, size = 40 }) => {
  const initials = (name || '??').slice(0, 2).toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899'];
  const color = palette[(name?.charCodeAt(0) || 0) % palette.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.35), flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

// ─── ChatHeadItem ──────────────────────────────────────────────────────────────
const ChatHeadItem = ({ head, isSelected, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 16px', cursor: 'pointer',
      background: isSelected ? '#eff6ff' : 'transparent',
      borderLeft: `3px solid ${isSelected ? '#3b82f6' : 'transparent'}`,
      transition: 'background 0.15s',
    }}
    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isSelected ? '#eff6ff' : 'transparent'; }}
  >
    <Avatar src={head.pictureUrl} name={head.displayName} size={44} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{
          fontWeight: 600, fontSize: 14, color: '#1f2937',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130,
        }}>
          {head.displayName}
        </span>
        {head.lastMessageDate && (
          <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0, marginLeft: 4 }}>
            {DateTimeAgoFormat(head.lastMessageDate)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
        {head.lastMessage || 'ยังไม่มีข้อความ'}
      </div>
    </div>
    {head.unreadCount > 0 && (
      <span style={{
        background: '#3b82f6', color: '#fff',
        borderRadius: 10, fontSize: 11, fontWeight: 700,
        padding: '2px 6px', flexShrink: 0,
      }}>
        {head.unreadCount}
      </span>
    )}
  </div>
);

// ─── MessageBubble ─────────────────────────────────────────────────────────────
const MessageBubble = ({ msg }) => {
  const isAdmin = !msg.isFromUser;
  
  // Check if message is a flex message
  let flexContent = null;
  if (msg.messageType === 'flex' && msg.rawPayload) {
    try {
      const payload = typeof msg.rawPayload === 'string' ? JSON.parse(msg.rawPayload) : msg.rawPayload;
      if (payload.messages?.[0]?.type === 'flex') {
        flexContent = payload.messages[0].contents;
      }
    } catch (e) {
      console.error('Failed to parse flex message:', e);
    }
  }
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: isAdmin ? 'flex-end' : 'flex-start',
      marginBottom: 4, padding: '0 16px',
    }}>
      <div style={{ maxWidth: flexContent ? '85%' : '68%' }}>
        {flexContent ? (
          <FlexMessageRenderer flexContent={flexContent} />
        ) : (
          <div style={{
            background: isAdmin ? '#1e3a5f' : '#f3f4f6',
            color: isAdmin ? '#fff' : '#1f2937',
            padding: '10px 14px',
            borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word',
          }}>
            {msg.imageUrl ? (
              <img 
                src={msg.imageUrl.startsWith('http') ? msg.imageUrl : `${process.env.REACT_APP_API_URL}${msg.imageUrl}`} 
                alt="Sent" 
                style={{ maxWidth: '100%', borderRadius: 8 }} 
              />
            ) : (
              msg.messageContent
            )}
          </div>
        )}
        <div style={{
          fontSize: 11, color: '#9ca3af', marginTop: 3,
          textAlign: isAdmin ? 'right' : 'left',
        }}>
          {DateTimeFormat(msg.createDate)}
        </div>
      </div>
    </div>
  );
};

// ─── AppChat ───────────────────────────────────────────────────────────────────
const AppChat = () => {
  const api = useApiServices();
  const { messages: signalRMessages } = useSignalR();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [chatHeads, setChatHeads] = useState([]);
  const [headsLoading, setHeadsLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');

  const [selectedHead, setSelectedHead] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const fetchHeads = async () => {
    try {
      const data = await api.GetChatHeadsAsync({ page: 1, pageSize: 100 });
      if (Array.isArray(data)) setChatHeads(data);
    } finally {
      setHeadsLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    setMsgLoading(true);
    try {
      const data = await api.GetChatMessagesAsync(userId, { page: 1, pageSize: 50 });
      if (Array.isArray(data)) {
        setChatMessages(data.sort((a, b) => new Date(a.createDate) - new Date(b.createDate)));
      }
    } catch {
      setChatMessages([]);
    } finally {
      setMsgLoading(false);
    }
  };

  const selectHead = (head) => {
    setSelectedHead(head);
    setChatMessages([]);
    fetchMessages(head.userId);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text || !selectedHead || sending) return;
    setSending(true);
    setNewMessage('');
    try {
      const id = await api.SendMessageAsync({ userId: selectedHead.userId, message: text });
      setChatMessages((prev) => [...prev, {
        id,
        userId: selectedHead.userId,
        messageContent: text,
        createDate: new Date().toISOString(),
        isFromUser: false,
      }]);
    } catch {
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedHead || sending) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('UserId', selectedHead.userId);
      formData.append('ImageFile', file);

      await api.SendImageAsync(formData);
      // Refresh messages to get the actual image data from server
      await fetchMessages(selectedHead.userId);
    } catch (error) {
      console.error('Failed to send image:', error);
      alert('ไม่สามารถส่งรูปภาพได้ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setSending(false);
      event.target.value = '';
    }
  };

  // Real-time SignalR
  useEffect(() => {
    if (!signalRMessages?.length) return;
    const lastEntry = signalRMessages[signalRMessages.length - 1];
    const last = typeof lastEntry === 'string' ? JSON.parse(lastEntry) : lastEntry;
    if (!last || last.Type !== 'Message') return;

    fetchHeads();

    if (last.From === 'User' && last.Keys?.[0] === selectedHead?.userId) {
      // If message is an image placeholder, refresh to get actual data
      if (last.Message === '[🖼️ รูปภาพ]' || last.Message === '[รูปภาพ]') {
        fetchMessages(selectedHead.userId);
        return;
      }
      
      setChatMessages((prev) => {
        if (prev.findIndex((m) => m.id === last.Id) >= 0) return prev;
        return [...prev, {
          id: last.Id,
          userId: selectedHead.userId,
          messageContent: last.Message,
          createDate: new Date().toISOString(),
          isFromUser: true,
        }];
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalRMessages]);

  // Snap to bottom on initial load
  useEffect(() => {
    if (!msgLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }
  }, [msgLoading]);

  // Smooth scroll for new incoming messages
  useEffect(() => {
    if (!msgLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, msgLoading]);

  useEffect(() => { 
    fetchHeads(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredHeads = chatHeads.filter((h) =>
    (h.displayName || '').toLowerCase().includes(searchKeyword.toLowerCase())
  );

  return (
    <div style={s.wrapper}>
      <div style={s.root}>
        {/* ── Sidebar ──────────────────────────────────────── */}
        <div style={s.sidebar}>
        <div style={s.sidebarHeader}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#1f2937' }}>แชท</h5>
          <span style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            {chatHeads.length} การสนทนา
          </span>
        </div>

        <div style={s.searchWrap}>
          <i className="fa fa-search" style={s.searchIcon} />
          <input
            type="text"
            placeholder="ค้นหาผู้ใช้..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={s.searchInput}
          />
        </div>

        <div style={s.headList}>
          {headsLoading ? (
            <div style={s.center}>
              <i className="fa fa-spinner fa-spin" style={{ color: '#9ca3af', fontSize: 20 }} />
            </div>
          ) : filteredHeads.length === 0 ? (
            <div style={s.center}>
              <i className="fas fa-comment-slash" style={{ color: '#d1d5db', fontSize: 28 }} />
              <span style={{ marginTop: 8, color: '#9ca3af', fontSize: 13 }}>ไม่พบการสนทนา</span>
            </div>
          ) : (
            filteredHeads.map((head) => (
              <ChatHeadItem
                key={head.userId}
                head={head}
                isSelected={selectedHead?.userId === head.userId}
                onClick={() => selectHead(head)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Chat Panel ───────────────────────────────────── */}
      <div style={s.panel}>
        {!selectedHead ? (
          <div style={s.center}>
            <div style={s.emptyIcon}>
              <i className="fa fa-comments" style={{ fontSize: 36, color: '#3b82f6' }} />
            </div>
            <h5 style={{ margin: '16px 0 8px', color: '#1f2937', fontWeight: 700 }}>เลือกการสนทนา</h5>
            <p style={{ margin: 0, color: '#9ca3af', fontSize: 14 }}>
              เลือกผู้ใช้จากรายการทางซ้ายเพื่อเริ่มแชท
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={s.panelHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar src={selectedHead.pictureUrl} name={selectedHead.displayName} size={40} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
                    {selectedHead.displayName}
                  </div>
                  {selectedHead.userId && (
                    <Link
                      to={`/users/${selectedHead.userId}`}
                      style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}
                    >
                      ดูโปรไฟล์ →
                    </Link>
                  )}
                </div>
              </div>
              <button
                style={s.iconBtn}
                onClick={() => fetchMessages(selectedHead.userId)}
                title="รีเฟรช"
              >
                <i className="fas fa-redo" />
              </button>
            </div>

            {/* Messages */}
            <div style={s.messages}>
              {msgLoading ? (
                <div style={s.center}>
                  <i className="fa fa-spinner fa-spin" style={{ color: '#9ca3af', fontSize: 24 }} />
                </div>
              ) : chatMessages.length === 0 ? (
                <div style={s.center}>
                  <i className="fa fa-comment" style={{ color: '#e5e7eb', fontSize: 32 }} />
                  <span style={{ marginTop: 8, color: '#9ca3af', fontSize: 13 }}>ยังไม่มีข้อความ</span>
                </div>
              ) : (
                chatMessages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={s.inputArea}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                style={{
                  ...s.imageBtn,
                  opacity: sending ? 0.45 : 1,
                  cursor: sending ? 'not-allowed' : 'pointer',
                }}
                title="ส่งรูปภาพ"
              >
                <i className="fa fa-image" />
              </button>
              <textarea
                ref={inputRef}
                rows={1}
                placeholder="พิมพ์ข้อความ... (Enter เพื่อส่ง, Shift+Enter ขึ้นบรรทัดใหม่)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                style={s.textarea}
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                style={{
                  ...s.sendBtn,
                  opacity: sending || !newMessage.trim() ? 0.45 : 1,
                  cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {sending
                  ? <i className="fa fa-spinner fa-spin" />
                  : <i className="fa fa-paper-plane" />
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    </div>
  );
};

const s = {
  wrapper: {
    padding: '20px 0',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  root: {
    display: 'flex',
    height: '75vh',
    maxHeight: '800px',
    minHeight: '500px',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
    background: '#fff',
    border: '1px solid #e5e7eb',
  },
  sidebar: {
    width: 300, flexShrink: 0,
    borderRight: '1px solid #e5e7eb',
    display: 'flex', flexDirection: 'column',
    background: '#fff',
  },
  sidebarHeader: {
    padding: '20px 16px 12px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex', flexDirection: 'column',
  },
  searchWrap: {
    position: 'relative',
    padding: '10px 12px',
    borderBottom: '1px solid #f3f4f6',
  },
  searchIcon: {
    position: 'absolute', left: 24, top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af', fontSize: 13, pointerEvents: 'none',
  },
  searchInput: {
    width: '100%', padding: '8px 12px 8px 32px',
    border: '1px solid #e5e7eb', borderRadius: 20,
    fontSize: 13, outline: 'none', background: '#f9fafb',
    boxSizing: 'border-box',
  },
  headList: { flex: 1, overflowY: 'auto' },
  panel: {
    flex: 1, display: 'flex', flexDirection: 'column',
    background: '#f9fafb', minWidth: 0,
  },
  panelHeader: {
    padding: '12px 20px',
    background: '#fff',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexShrink: 0,
  },
  messages: {
    flex: 1, overflowY: 'auto',
    padding: '16px 0',
    display: 'flex', flexDirection: 'column',
  },
  inputArea: {
    padding: '12px 16px',
    background: '#fff', borderTop: '1px solid #e5e7eb',
    display: 'flex', alignItems: 'flex-end', gap: 10,
    flexShrink: 0,
  },
  textarea: {
    flex: 1, resize: 'none',
    border: '1.5px solid #e5e7eb', borderRadius: 20,
    padding: '10px 16px', fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
    lineHeight: 1.5, maxHeight: 120, overflowY: 'auto',
  },
  sendBtn: {
    width: 44, height: 44, borderRadius: '50%',
    background: '#1e3a5f', color: '#fff',
    border: 'none', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 16, flexShrink: 0,
    transition: 'opacity 0.15s',
  },
  imageBtn: {
    width: 44, height: 44, borderRadius: '50%',
    background: '#f3f4f6', color: '#6b7280',
    border: 'none', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 16, flexShrink: 0,
    transition: 'all 0.15s',
    cursor: 'pointer',
  },
  iconBtn: {
    background: 'none', border: 'none',
    color: '#9ca3af', cursor: 'pointer',
    fontSize: 14, padding: 8, borderRadius: 6,
  },
  center: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: 32,
  },
  emptyIcon: {
    width: 72, height: 72, borderRadius: '50%',
    background: '#eff6ff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
}; 
export default AppChat;
